<template>
  <div v-if="accessAllowed" v-rect-loading="pageLoading" class="ai-chatbot-config-management">
    <div class="page-header">
      <div>
        <div class="page-title">{{ $t(`${i18nPrefix}.title.index`) }}</div>
        <div class="page-description">{{ $t(`${i18nPrefix}.label.pageDescription`) }}</div>
      </div>
      <el-button class="save-btn" type="primary" @click="handleSave">{{ $t('common.btn.save') }}</el-button>
    </div>

    <div class="content-section">
      <div class="section-title">{{ $t(`${i18nPrefix}.label.basicConfig`) }}</div>
      <WrappedForm
        ref="basicConfigForm"
        class="config-form"
        :model="formData"
        :formLines="formLines"
      >
        <template #avatar>
          <div class="avatar-upload-wrapper">
            <el-upload
              class="avatar-uploader"
              action=""
              accept=".jpg,.jpeg,.png,.gif"
              :show-file-list="false"
              :before-upload="handleBeforeAvatarUpload"
              :http-request="handleAvatarUpload"
            >
              <div class="avatar-preview">
                <img v-if="avatarUrl" :src="avatarUrl" alt="avatar" class="avatar-image" />
                <div v-else class="avatar-placeholder">
                  <i class="el-icon-plus"></i>
                </div>
                <div class="avatar-mask">{{ $t(`${i18nPrefix}.btn.uploadAvatar`) }}</div>
              </div>
            </el-upload>
            <div class="avatar-tip">{{ $t(`${i18nPrefix}.label.avatarTip`) }}</div>
          </div>
        </template>
        <template #themeColor>
          <div class="theme-color-row">
            <el-input v-model.trim="formData.theme_color" class="theme-color-input" :placeholder="$t(`${i18nPrefix}.placeholder.themeColor`)"></el-input>
            <el-color-picker v-model="formData.theme_color"></el-color-picker>
          </div>
        </template>
      </WrappedForm>
    </div>

    <div class="content-section">
      <div class="section-title">{{ $t(`${i18nPrefix}.label.triggerConfig`) }}</div>
      <div class="trigger-section" v-for="trigger in triggerDefinitions" :key="trigger.key">
        <div class="trigger-header">
          <div class="trigger-title">{{ $t(`${i18nPrefix}.label.${trigger.labelKey}`) }}</div>
          <div class="trigger-switch">
            <span class="trigger-switch-label">{{ $t(`${i18nPrefix}.label.enabled`) }}</span>
            <el-switch v-model="formData[trigger.enabledField]"></el-switch>
          </div>
        </div>

        <div class="trigger-body" :class="{ 'is-disabled': !formData[trigger.enabledField] }">
          <WrappedForm
            ref="form"
            class="trigger-form"
            labelWidth="150px"
            :disabled="!formData[trigger.enabledField]"
            :model="formData[trigger.configField].message"
            :formLines="triggerMessageFormLines"
          ></WrappedForm>

          <div class="trigger-action-list">
            <div class="trigger-action-list-header">
              <div class="action-card-title">{{ $t(`${i18nPrefix}.label.actionConfig`) }}</div>
              <el-button
                size="mini"
                type="primary"
                plain
                icon="el-icon-plus"
                :disabled="!formData[trigger.enabledField]"
                @click="handleAddTriggerAction(trigger.configField)"
              >
                {{ $t(`${i18nPrefix}.btn.addAction`) }}
              </el-button>
            </div>

            <draggable
              :list="formData[trigger.configField].actions"
              class="trigger-action-draggable-list"
              handle=".draggable-handle"
              :disabled="!formData[trigger.enabledField]"
            >
              <div
                v-for="(action, actionIndex) in formData[trigger.configField].actions"
                :key="`${trigger.key}-${actionIndex}`"
                class="trigger-action-card"
              >
                <div class="trigger-action-card-header">
                  <div class="trigger-action-card-title-wrapper">
                    <i class="el-icon-rank draggable-handle" :class="{ 'is-disabled': !formData[trigger.enabledField] }"></i>
                    <div class="trigger-action-card-title">
                      {{ $t(`${i18nPrefix}.label.actionIndex`, { index: actionIndex + 1 }) }}
                    </div>
                  </div>
                  <el-button
                    type="text"
                    icon="el-icon-delete"
                    :disabled="!formData[trigger.enabledField]"
                    @click="handleRemoveTriggerAction(trigger.configField, actionIndex)"
                  >
                    {{ $t(`${i18nPrefix}.btn.deleteAction`) }}
                  </el-button>
                </div>

                <WrappedForm
                  ref="form"
                  class="trigger-form"
                  labelWidth="150px"
                  :disabled="!formData[trigger.enabledField]"
                  :model="action.label"
                  :formLines="triggerActionLabelFormLines"
                ></WrappedForm>

                <WrappedForm
                  ref="form"
                  class="trigger-form"
                  labelWidth="150px"
                  :disabled="!formData[trigger.enabledField]"
                  :model="action"
                  :formLines="getTriggerActionFormLines(action)"
                >
                  <template #value>
                    <el-select
                      v-if="isTriggerEventAction(action.type)"
                      v-model="action.value"
                      class="full-width"
                      :disabled="!formData[trigger.enabledField]"
                      :placeholder="$t(`${i18nPrefix}.placeholder.actionValueSelect`)"
                    >
                      <el-option
                        v-for="option in triggerEventValueOptions"
                        :key="option.value"
                        :label="option.label"
                        :value="option.value"
                      ></el-option>
                    </el-select>
                    <el-input
                      v-else
                      v-model.trim="action.value"
                      :maxlength="200"
                      show-word-limit
                      :disabled="!formData[trigger.enabledField]"
                      :placeholder="$t(`${i18nPrefix}.placeholder.actionValueInput`)"
                    ></el-input>
                  </template>
                </WrappedForm>
              </div>
            </draggable>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import draggable from 'vuedraggable'
import { getFilePath, uploadFile } from '@/utils/qiniu'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import { ACTION_TRIGGER_EVENT_APPLY_JOB, ACTION_TYPE_LINK_REDIRECTION, ACTION_TYPE_LIST, ACTION_TYPE_SEND_CHATBOT_MESSAGE, ACTION_TYPE_TRIGGER_EVENT } from '@/constant/aiChatbot'
import _ from 'lodash'

const MAX_TRIGGER_ACTIONS = 5
const IMAGE_MAX_SIZE_MB = 2
const IMAGE_MAX_SIZE = IMAGE_MAX_SIZE_MB * 1024 * 1024

export default {
  components: {
    draggable,
    WrappedForm
  },
  data () {
    return {
      MAX_TRIGGER_ACTIONS,
      ACTION_TYPE_LINK_REDIRECTION,
      ACTION_TYPE_SEND_CHATBOT_MESSAGE,
      ACTION_TYPE_TRIGGER_EVENT,
      i18nPrefix: 'admin.company.aiChatbotConfigManagement',
      accessAllowed: false,
      pageLoading: false,
      avatarUploading: false,
      pendingAvatarFile: null,
      avatarPreviewUrl: null,
      formData: {
        welcome_trigger_config: {
          message: {}
        },
        welcome_back_trigger_config: {
          message: {}
        },
        stay_30s_trigger_config: {
          message: {}
        }
      }
    }
  },
  computed: {
    ...mapGetters('account', ['getCurrentAccount']),
    avatarUrl () {
      return this.avatarPreviewUrl || getFilePath(this.formData.avatar)
    },
    currentAccount () {
      return this.getCurrentAccount
    },
    formLines () {
      return {
        avatar: {
          label: this.$t(`${this.i18nPrefix}.label.avatar`),
          contentSlot: 'avatar',
          rules: [{ validator: this.validateAvatar, trigger: 'change' }]
        },
        name: {
          label: this.$t(`${this.i18nPrefix}.label.name`),
          props: {
            maxlength: 100,
            showWordLimit: true
          },
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        name_cn: {
          label: this.$t(`${this.i18nPrefix}.label.nameCn`),
          props: {
            maxlength: 100,
            showWordLimit: true
          },
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        welcome_message: {
          label: this.$t(`${this.i18nPrefix}.label.welcomeMessage`),
          props: {
            type: 'textarea',
            rows: 3,
            maxlength: 300,
            showWordLimit: true
          },
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        welcome_message_cn: {
          label: this.$t(`${this.i18nPrefix}.label.welcomeMessageCn`),
          props: {
            type: 'textarea',
            rows: 3,
            maxlength: 300,
            showWordLimit: true
          },
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        theme_color: {
          label: this.$t(`${this.i18nPrefix}.label.themeColor`),
          contentSlot: 'themeColor',
          rules: [{ validator: this.validateHexColor, trigger: 'change' }]
        }
      }
    },
    triggerDefinitions () {
      return [
        {
          key: 'welcome',
          labelKey: 'welcomeTrigger',
          enabledField: 'welcome_trigger_enabled',
          configField: 'welcome_trigger_config'
        },
        {
          key: 'welcomeBack',
          labelKey: 'welcomeBackTrigger',
          enabledField: 'welcome_back_trigger_enabled',
          configField: 'welcome_back_trigger_config'
        },
        {
          key: 'stay30s',
          labelKey: 'stay30sTrigger',
          enabledField: 'stay_30s_trigger_enabled',
          configField: 'stay_30s_trigger_config'
        }
      ]
    },
    triggerMessageFormLines () {
      return {
        zh: {
          label: this.$t(`${this.i18nPrefix}.label.messageZh`),
          props: {
            type: 'textarea',
            rows: 3,
            maxlength: 300,
            showWordLimit: true
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.messageZh`),
          rules: [{ required: true, message: this.$t(`${this.i18nPrefix}.message.triggerMessageRequired`), trigger: 'blur' }]
        },
        en: {
          label: this.$t(`${this.i18nPrefix}.label.messageEn`),
          props: {
            type: 'textarea',
            rows: 3,
            maxlength: 300,
            showWordLimit: true
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.messageEn`),
          rules: [{ required: true, message: this.$t(`${this.i18nPrefix}.message.triggerMessageRequired`), trigger: 'blur' }]
        }
      }
    },
    triggerActionLabelFormLines () {
      return {
        zh: {
          label: this.$t(`${this.i18nPrefix}.label.actionLabelZh`),
          props: {
            maxlength: 80,
            showWordLimit: true
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.actionLabelZh`),
          rules: [{ required: true, message: this.$t(`${this.i18nPrefix}.message.triggerActionLabelRequired`), trigger: 'blur' }]
        },
        en: {
          label: this.$t(`${this.i18nPrefix}.label.actionLabelEn`),
          props: {
            maxlength: 80,
            showWordLimit: true
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.actionLabelEn`),
          rules: [{ required: true, message: this.$t(`${this.i18nPrefix}.message.triggerActionLabelRequired`), trigger: 'blur' }]
        }
      }
    },
    actionTypeOptions () {
      return [
        {
          value: ACTION_TYPE_SEND_CHATBOT_MESSAGE,
          label: this.$t(`${this.i18nPrefix}.option.sendChatbotMessage`)
        },
        {
          value: ACTION_TYPE_LINK_REDIRECTION,
          label: this.$t(`${this.i18nPrefix}.option.linkRedirection`)
        }
      ]
    },
    triggerEventValueOptions () {
      return [{
        value: ACTION_TRIGGER_EVENT_APPLY_JOB,
        label: this.$t(`${this.i18nPrefix}.option.applyJob`)
      }]
    }
  },
  created () {
    this.initData()
    window.addEventListener('focus', this.checkAccess)
  },
  methods: {
    ...mapActions('aiChatbot', ['getAiChatbotConfig', 'updateAiChatbotConfig']),
    async initData () {
      this.pageLoading = true
      try {
        await this.loadConfig()
      } catch (error) {
        if (error.code === 10001005) {
          this.accessAllowed = false
          await this.$router.replace('/error/no-permission')
          return
        }
        throw error
      } finally {
        this.pageLoading = false
      }
    },
    async checkAccess () {
      try {
        const config = await this.getAiChatbotConfig()
        this.accessAllowed = true
        return config
      } catch (error) {
        this.accessAllowed = false
        if (error.code === 10001005) {
          await this.$router.replace('/error/no-permission')
          return null
        }
        throw error
      }
    },
    async loadConfig () {
      const resp = await this.checkAccess()
      if (!resp) {
        return
      }
      this.clearPendingAvatar()
      this.formData = resp
      this.$nextTick(() => {
        this.$refs.basicConfigForm.clearValidate()
        _.invokeMap(this.$refs.form, 'clearValidate')
      })
    },
    validateAvatar (rule, value, callback) {
      if (!this.pendingAvatarFile && !this.formData.avatar) {
        callback(new Error(this.$t('common.message.requiredFormField')))
        return
      }
      callback()
    },
    validateHexColor (rule, value, callback) {
      if (!value) {
        callback(new Error(this.$t('common.message.requiredFormField')))
        return
      }
      if (!/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(value)) {
        callback(new Error(this.$t(`${this.i18nPrefix}.message.invalidThemeColor`)))
        return
      }
      callback()
    },
    handleBeforeAvatarUpload (file) {
      if (!/image/.test(file.type)) {
        this.$message.error(this.$t('common.message.wrongImageType'))
        return false
      }
      if (file.size > IMAGE_MAX_SIZE) {
        this.$message.error(this.$t('common.message.wrongImageSize', { size: IMAGE_MAX_SIZE_MB }))
        return false
      }
      return true
    },
    async handleAvatarUpload (fileInfo) {
      this.clearAvatarPreviewUrl()
      this.pendingAvatarFile = fileInfo.file
      this.avatarPreviewUrl = URL.createObjectURL(fileInfo.file)
      this.$refs.basicConfigForm.validateField('avatar')
    },
    generateAvatarFileKey (file) {
      const rootCompanyId = this.currentAccount.root_company_id || this.currentAccount.company_id || 'company'
      const ext = file.name && file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')) : ''
      return `company/${rootCompanyId}/ai-chatbot/avatar/${new Date().valueOf()}${ext}`
    },
    clearAvatarPreviewUrl () {
      if (this.avatarPreviewUrl) {
        URL.revokeObjectURL(this.avatarPreviewUrl)
        this.avatarPreviewUrl = null
      }
    },
    clearPendingAvatar () {
      this.pendingAvatarFile = null
      this.clearAvatarPreviewUrl()
    },
    async validateForm () {
      if (_.some(this.triggerDefinitions, trigger => this.formData[trigger.configField].actions.length > MAX_TRIGGER_ACTIONS)) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.actionLimit`, { max: MAX_TRIGGER_ACTIONS }))
        return false
      }
      const valid = await this.$refs.basicConfigForm.submit()
      const forms = _.filter(_.castArray(this.$refs.form), form => !form.disabled)
      const results = await Promise.all(_.invokeMap(forms, 'submit'))
      return _.every(results) && valid
    },
    getTriggerActionFormLines (action) {
      return {
        type: {
          label: this.$t(`${this.i18nPrefix}.label.actionType`),
          elType: 'el-select',
          option: {
            data: this.actionTypeOptions
          },
          props: {
            clearable: true
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.actionType`),
          rules: [
            { required: true, message: this.$t('common.message.requiredFormField'), trigger: 'change' },
            { type: 'enum', enum: ACTION_TYPE_LIST, message: this.$t(`${this.i18nPrefix}.message.triggerActionUnsupported`), trigger: 'change' }
          ],
          events: {
            change: (selectedType) => this.handleTriggerActionTypeChange(action, selectedType)
          }
        },
        value: {
          hidden: !action.type || action.type === ACTION_TYPE_SEND_CHATBOT_MESSAGE,
          label: this.$t(`${this.i18nPrefix}.label.actionValue`),
          contentSlot: 'value',
          rules: [{ required: true, message: this.$t(`${this.i18nPrefix}.message.triggerActionValueRequired`), trigger: 'blur' }]
        }
      }
    },
    isTriggerEventAction (type) {
      return type === ACTION_TYPE_TRIGGER_EVENT
    },
    handleTriggerActionTypeChange (action, type) {
      if (this.isTriggerEventAction(type)) {
        action.value = ACTION_TRIGGER_EVENT_APPLY_JOB
        return
      }
      action.value = null
    },
    handleAddTriggerAction (configField) {
      if (this.formData[configField].actions.length >= MAX_TRIGGER_ACTIONS) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.actionLimit`, { max: MAX_TRIGGER_ACTIONS }))
        return
      }
      this.formData[configField].actions.push(this.createDefaultTriggerAction())
    },
    handleRemoveTriggerAction (configField, actionIndex) {
      this.formData[configField].actions.splice(actionIndex, 1)
    },
    buildTriggerPayload (configField) {
      const config = this.formData[configField]
      return {
        message: {
          zh: config.message.zh.trim(),
          en: config.message.en.trim()
        },
        actions: _.map(config.actions, action => ({
          label: {
            zh: action.label.zh.trim(),
            en: action.label.en.trim()
          },
          type: action.type,
          value: `${action.value || ''}`.trim()
        }))
      }
    },
    async handleSave () {
      if (!await this.checkAccess()) {
        return
      }
      const valid = await this.validateForm()
      if (!valid) {
        return
      }

      this.$eventBus.$emit('showLoading', true)
      try {
        let avatar = this.formData.avatar
        if (this.pendingAvatarFile) {
          this.avatarUploading = true
          try {
            const fileKey = this.generateAvatarFileKey(this.pendingAvatarFile)
            const resp = await uploadFile(this.pendingAvatarFile, fileKey)
            avatar = resp && resp.key ? resp.key : fileKey
          } catch (error) {
            this.$message.error(this.$t('common.message.uploadError'))
            return
          } finally {
            this.avatarUploading = false
          }
        }

        await this.updateAiChatbotConfig({
          avatar,
          name: this.formData.name,
          name_cn: this.formData.name_cn,
          welcome_message: this.formData.welcome_message,
          welcome_message_cn: this.formData.welcome_message_cn,
          theme_color: this.formData.theme_color,
          welcome_trigger_enabled: this.formData.welcome_trigger_enabled,
          welcome_trigger_config: this.buildTriggerPayload('welcome_trigger_config'),
          welcome_back_trigger_enabled: this.formData.welcome_back_trigger_enabled,
          welcome_back_trigger_config: this.buildTriggerPayload('welcome_back_trigger_config'),
          stay_30s_trigger_enabled: this.formData.stay_30s_trigger_enabled,
          stay_30s_trigger_config: this.buildTriggerPayload('stay_30s_trigger_config')
        })
        this.$message.success(this.$t('common.message.saveSuccess'))
        await this.loadConfig()
      } catch (error) {
        if (error.code === 10001005) {
          this.accessAllowed = false
          await this.$router.replace('/error/no-permission')
          return
        }
        throw error
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    createDefaultTriggerAction () {
      return {
        label: {
          zh: null,
          en: null
        },
        type: ACTION_TYPE_SEND_CHATBOT_MESSAGE,
        value: null
      }
    }
  },
  beforeDestroy () {
    window.removeEventListener('focus', this.checkAccess)
    this.clearPendingAvatar()
  }
}
</script>

<style lang="scss" scoped>
.ai-chatbot-config-management {
  min-height: 100%;

  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 24px;

    .page-title {
      font-size: 20px;
      line-height: 28px;
      color: #303133;
      font-weight: 600;
    }

    .page-description {
      margin-top: 8px;
      color: #606266;
    }
  }

  .content-section {
    padding: 24px;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    background: #ffffff;

    & + .content-section {
      margin-top: 20px;
    }

    .section-title {
      margin-bottom: 20px;
      font-size: 16px;
      line-height: 24px;
      color: #303133;
      font-weight: 600;
    }

    .config-form {
      max-width: 860px;
      margin: auto;
    }

    .avatar-upload-wrapper {
      display: flex;
      align-items: center;

      .avatar-tip {
        margin-left: 16px;
        color: #909399;
      }
    }

    .avatar-preview {
      position: relative;
      width: 120px;
      height: 120px;
      border: 1px dashed #dcdfe6;
      border-radius: 8px;
      overflow: hidden;
      background: #f5f7fa;
      cursor: pointer;

      &:hover {
        .avatar-mask {
          opacity: 1;
        }
      }

      .avatar-image,
      .avatar-placeholder {
        width: 100%;
        height: 100%;
      }

      .avatar-image {
        object-fit: contain;
      }

      .avatar-placeholder {
        display: flex;
        align-items: center;
        justify-content: center;
        color: #c0c4cc;
        font-size: 28px;
      }

      .avatar-mask {
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        padding: 8px 12px;
        background: rgba(0, 0, 0, 0.55);
        color: #ffffff;
        text-align: center;
        opacity: 0;
        transition: opacity 0.2s ease;
      }
    }

    .theme-color-row {
      display: flex;
      align-items: center;

      .theme-color-input {
        width: 220px;
        margin-right: 12px;
      }
    }

    .trigger-section {
      padding: 20px;
      border: 1px solid #ebeef5;
      border-radius: 8px;

      & + .trigger-section {
        margin-top: 16px;
      }

      .trigger-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 16px;

        .trigger-title {
          font-size: 15px;
          font-weight: 600;
          color: #303133;
        }

        .trigger-switch {
          display: flex;
          align-items: center;
          gap: 12px;

          .trigger-switch-label {
            color: #606266;
          }
        }
      }

      .trigger-body {
        &.is-disabled {
          opacity: 0.56;
        }
      }

      .trigger-form {
        max-width: initial;
      }

      .trigger-action-list {
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid #ebeef5;

        .trigger-action-list-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;

          .action-card-title {
            color: #606266;
            font-weight: 600;
          }
        }

        .trigger-action-card {
          margin-top: 16px;
          padding: 16px;
          border: 1px solid #ebeef5;
          border-radius: 8px;
          background: #fafbfd;

          .trigger-action-card-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
          }

          .trigger-action-card-title-wrapper {
            display: flex;
            align-items: center;
            gap: 8px;
            min-width: 0;
          }

          .trigger-action-card-title {
            color: #303133;
            font-weight: 600;
          }
        }
      }
    }

    .draggable-handle {
      color: #909399;
      cursor: grab;
      font-size: 16px;

      &.is-disabled {
        cursor: not-allowed;
        color: #c0c4cc;
      }
    }

    .full-width {
      width: 100%;
    }
  }

  ::v-deep {
    .trigger-form {
      .el-form {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 16px;
      }

      .el-textarea,
      .el-input,
      .el-select {
        width: 100%;
      }
    }

    .el-input-number {
      width: 220px;

      .el-input {
        .el-input__inner {
          padding-right: 20px;
        }
      }
    }
  }
}
</style>
